import { IMG, VIDEO } from "@/lib/images";

export const TEAMS = {
  marketing: {
    slug: "marketing",
    heroImage: IMG.marketingHero,
    video: VIDEO.marketing,
    craftHighlight: {
      label: "Our most-requested install",
      head: "AI avatar spokesperson video, on autopilot.",
      body: "A branded AI avatar puts your business on camera every day — turning market updates and offers into short-form video that auto-publishes across every platform. Your brand on camera, without you on camera.",
    },
    eyebrow: "MARKETING & SALES",
    h1: "Your demand, handled.",
    subline:
      "Paid social, outreach, follow-up, and reactivation — run continuously as your industry's playbook, not in scattered bursts.",
    problemsHead: "Leads go cold while the budget burns.",
    problems: [
      "Leads go cold before anyone follows up.",
      "Ad budget burns with no system behind it.",
      "Outreach is inconsistent and nobody owns the pipeline.",
    ],
    leader: {
      name: "Daniyal Syed",
      title: "Growth Team lead",
      photo: "https://customer-assets.emergentagent.com/job_human-layer-demo/artifacts/n2q1u125_IMG_7186.jpg",
      bio: "A senior growth marketer who's run paid + lifecycle across verticals. He directs the Growth Agent and answers for every number.",
    },
    outcomes: [
      "Faster speed-to-lead",
      "Lower cost per lead",
      "More qualified pipeline",
      "Dead leads reactivated",
      "Reporting pushed to you weekly",
    ],
    portfolio: [
      { label: "Open-house funnels", vertical: "Real Estate", img: IMG.solRealEstate },
      { label: "Intake campaigns", vertical: "Legal", img: IMG.solLegal },
      { label: "Retargeting engine", vertical: "Automotive", img: IMG.solAutomotive },
      { label: "Booking flows", vertical: "Home Services", img: IMG.solHomeServices },
      { label: "Patient acquisition", vertical: "Healthcare", img: IMG.solHealthcare },
    ],
    included: [
      "Paid social management",
      "Cold + warm outreach",
      "Email / SMS nurture",
      "Vertical playbooks",
      "Attribution + reporting",
    ],
    includedNote: "Every message cleared by a human before send.",
    cases: [
      {
        metric: "+60%",
        label: "Qualified pipeline in 90 days",
        quote:
          "For the first time I know which campaigns drive closed revenue. That visibility changed how I run the business.",
        name: "Ethan P.",
        title: "Head of Growth",
        photo: IMG.clientEthan,
      },
      {
        metric: "−38%",
        label: "Lower cost per lead in two months",
        quote:
          "We stopped guessing. The playbook runs every day and the cost per qualified lead just keeps dropping.",
        name: "Alexander W.",
        title: "Founder",
        photo: IMG.clientAlex,
      },
    ],
  },

  creative: {
    slug: "creative",
    heroImage: IMG.creativeHero,
    video: VIDEO.creative,
    craftHighlight: {
      label: "The studio's signature",
      head: "AI avatar and spokesperson video, at scale.",
      body: "Ad creative, motion, and brand voice — plus avatar-led spokesperson video produced daily with the best tools in the world. Nothing ships until a human clears it.",
    },
    eyebrow: "CREATIVE",
    h1: "Work that doesn't look AI-made.",
    subline:
      "High-performing ad creative and motion video, produced at scale with the best tools in the world — and the taste to keep it off-brand-proof.",
    problemsHead: "Scale shouldn't mean slop.",
    problems: [
      "AI content looks like slop.",
      "Brand drifts across channels.",
      "Production is too slow to feed the ad machine.",
    ],
    leader: {
      name: "Nabab Uddin",
      title: "Creative lead",
      photo: "https://customer-assets.emergentagent.com/job_human-layer-demo/artifacts/86ey59l3_IMG_7184.jpg",
      bio: "A creative director and social media influencer who lives in the best AI motion tools and refuses to ship slop. He directs the Studio Agent.",
    },
    outcomes: [
      "Ad creative that converts",
      "Motion video at scale",
      "On-brand every time",
      "Faster production cycles",
    ],
    portfolio: [
      { label: "Ad reels", vertical: "Automotive", img: IMG.solAutomotive },
      { label: "Short-form video", vertical: "Home Services", img: IMG.solHomeServices },
      { label: "Listing videos", vertical: "Real Estate", img: IMG.solRealEstate },
      { label: "Motion graphics", vertical: "Healthcare", img: IMG.solHealthcare },
      { label: "Brand systems", vertical: "Legal", img: IMG.solLegal },
    ],
    included: [
      "Static + video ad creative",
      "Short-form social video",
      "Motion graphics",
      "Brand-voice system",
      "Creative testing",
    ],
    includedNote:
      "Nothing ships until a human clears it — that's the no-slop guarantee.",
    cases: [
      {
        metric: "2.4×",
        label: "Creative-led performance lift",
        quote:
          "The new creative didn't just look better — it more than doubled our ad performance without raising spend.",
        name: "Darius F.",
        title: "CEO",
        photo: IMG.clientDarius,
      },
      {
        metric: "5 days",
        label: "From brief to a full library of ads",
        quote:
          "We used to wait weeks for one batch. Now a full set of on-brand ads lands in days, every week.",
        name: "Ethan P.",
        title: "Head of Growth",
        photo: IMG.clientEthan,
      },
    ],
  },

  engineering: {
    slug: "engineering",
    heroImage: IMG.engineeringHero,
    video: VIDEO.engineering,
    craftHighlight: {
      label: "What this team does",
      head: "We map how you run, then automate the slow parts.",
      body: "Workflow mapping and optimization, process automation, tool and CRM integration, custom internal tools, and the auto-publishing and data pipelines the other teams run on — no rip-and-replace, every automation reviewed by a human before it goes live.",
    },
    eyebrow: "WORKFLOW & AUTOMATION",
    h1: "We find the bottlenecks and automate them away.",
    subline:
      "We map how your business actually runs, fix what's slow, and wire AI into the tools you already use — so the whole operation moves faster without you rebuilding anything.",
    problemsHead: "Work slips between disconnected tools.",
    problems: [
      "Work falls through the cracks between tools.",
      "Manual steps eat hours every week.",
      "Your systems don't talk to each other, so nothing is ever in one place.",
    ],
    leader: {
      name: "Chris D.",
      title: "Workflow & Automation lead",
      photo: IMG.opChris,
      bio: "An engineer who maps and automates how operators actually run — CRM hygiene, lead routing, and workflow automation. He directs the Workflow Agent.",
    },
    outcomes: [
      "Hours of manual work removed",
      "Tools connected into one flow",
      "Bottlenecks found and fixed",
      "Automations running 24/7",
      "A business that runs without you babysitting it",
    ],
    portfolio: [
      { label: "Automation workflows", vertical: "Home Services", img: IMG.solHomeServices },
      { label: "Custom dashboards", vertical: "Healthcare", img: IMG.solHealthcare },
      { label: "Integration maps", vertical: "Automotive", img: IMG.solAutomotive },
      { label: "Lead routing", vertical: "Real Estate", img: IMG.solRealEstate },
      { label: "Compliant pipelines", vertical: "Legal", img: IMG.solLegal },
    ],
    included: [
      "Workflow mapping & optimization",
      "Process automation",
      "Tool & CRM integration",
      "Custom internal tools",
      "Auto-publishing & data pipelines",
    ],
    includedNote:
      "Works with the tools you already use — GoHighLevel, your CRM, email, calendar — no rip-and-replace. Every automation reviewed by a human before it goes live.",
    cases: [
      {
        metric: "3 wks",
        label: "To full deployment (vs. a 2-year internal attempt)",
        quote:
          "We tried to build this ourselves for two years — none of it connected. The AI Dept deployed it in three weeks.",
        name: "Darius F.",
        title: "CEO",
        photo: IMG.clientDarius,
      },
      {
        metric: "20 hrs",
        label: "Of manual work removed every week",
        quote:
          "The automations quietly took over the busywork. My team finally spends its time on customers, not copy-paste.",
        name: "Alexander W.",
        title: "Founder",
        photo: IMG.clientAlex,
      },
    ],
  },
};
